import { ConfirmReserveModal } from '_/components';
import { colors } from '_/constants/theme';
import { useDateContext } from '_/hooks/DateContext';
import { useEventContext } from '_/hooks/EventContext';
import { useSlotContext } from '_/hooks/SlotContext';
import { useSpaceContext } from '_/hooks/SpaceContext';
import NoAvailableSlots from '_/screens/Workstation/StatusTab/AvailableList/NoAvailableSlots';
import { RecurrenceType } from '_/services/models/enums/recurrence-type.enum';
import { SlotsModel } from '_/services/models/slots.model';
import { startOfDay, parseISO, endOfDay } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, RefreshControl, SectionList } from 'react-native';

import MeetingRoom from '../MeetingRoom';
import WeekCalendar from '../WeekCalendar';
import WebWeekCalendar from '../WeekCalendar/WebWeekCalendar';

function LoadingIndicator() {
  return <ActivityIndicator color={colors.black} size={35} style={{ marginTop: 20 }} />;
}

export default function RoomList() {
  const [rooms, setRooms] = useState<SlotsModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarVisibleId, setCalendarVisibleId] = useState('');
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(RecurrenceType.ONCE);

  const {
    selectedDay,
    timePickerVisible,
    selectedEventDate,
    checkIfThereIsConflict,
    setSlotWithConflicts,
    setHasConflict,
  } = useDateContext();
  const { getSpaceEvents, setMeetingReservations, meetingEvents } = useEventContext();
  const { spaceId } = useSpaceContext();
  const { isSlotModalVisible, setIsSlotModalVisible } = useSlotContext();

  const getReservations = useCallback(async () => {
    const data = await getSpaceEvents({
      spaceId,
      startAtTime: startOfDay(parseISO(selectedDay)),
      endAtTime: endOfDay(parseISO(selectedDay)),
    });

    const slots =
      data?.slots?.map((room: SlotsModel) => ({
        ...room,
        amenities: [...(room.amenities ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
      })) ?? [];

    setRooms(slots);
    setLoading(false);
  }, [getSpaceEvents, selectedDay, spaceId]);

  useEffect(() => {
    if (!rooms?.length) return;
    const filtered = rooms?.filter((s: SlotsModel) =>
      timePickerVisible ? true : s.id === calendarVisibleId
    );
    const eventsForThisDate = filtered?.flatMap((s: SlotsModel) => s.events?.map((e) => e) ?? []);
    setMeetingReservations(eventsForThisDate as any);
  }, [rooms, calendarVisibleId, timePickerVisible, setMeetingReservations]);

  useEffect(() => {
    setLoading(true);
    setRooms([]);
  }, [selectedDay]);

  useEffect(() => {
    getReservations();
  }, [getReservations]);

  useEffect(() => {
    checkIfThereIsConflict(selectedEventDate, meetingEvents);
  }, [checkIfThereIsConflict, meetingEvents, selectedEventDate]);

  const sections = useMemo(() => {
    return rooms?.map((room: SlotsModel) => ({
      title: room.name,
      data: [room],
    }));
  }, [rooms]);

  useEffect(() => {
    if (rooms.length === 1) {
      setCalendarVisibleId(rooms[0].id);
    }
  }, [rooms]);

  const renderItem = useCallback(
    ({ item }: { item: SlotsModel }) => {
      const visible = !timePickerVisible && calendarVisibleId === item.id;

      if (!visible) return null;

      return Platform.OS === 'web' ? (
        <WebWeekCalendar visible slot={item} />
      ) : (
        <WeekCalendar visible slot={item} />
      );
    },
    [calendarVisibleId, timePickerVisible]
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: { data: SlotsModel[] } }) => {
      const room = section.data[0];

      return (
        <MeetingRoom
          slot={room}
          amenities={room.amenities}
          calendarVisibleId={calendarVisibleId}
          setCalendarVisibleId={setCalendarVisibleId}
        />
      );
    },
    [calendarVisibleId]
  );

  const slotOnModal = useMemo(
    () => rooms?.find((room: SlotsModel) => room.id === isSlotModalVisible),
    [rooms, isSlotModalVisible]
  );

  const handleCloseConfirmationModal = useCallback(() => {
    setIsSlotModalVisible('');
    setSlotWithConflicts([]);
    setHasConflict(false);
    setRecurrenceType(RecurrenceType.ONCE);
  }, [setHasConflict, setIsSlotModalVisible, setSlotWithConflicts]);

  const onRefresh = useCallback(async () => {
    await getReservations();
  }, [getReservations]);

  const refreshControl = useMemo(
    () => <RefreshControl refreshing={false} onRefresh={onRefresh} />,
    [onRefresh]
  );

  return (
    <>
      {loading ? (
        <LoadingIndicator />
      ) : (
        <SectionList
          key="RoomList"
          initialNumToRender={30}
          maxToRenderPerBatch={30}
          updateCellsBatchingPeriod={50}
          keyExtractor={(item) => item.id}
          renderSectionHeader={renderSectionHeader}
          renderItem={renderItem}
          ListEmptyComponent={<NoAvailableSlots maxCapacityReached={false} notAllowed />}
          stickySectionHeadersEnabled
          sections={sections}
          showsVerticalScrollIndicator={false}
          refreshControl={refreshControl}
          extraData={calendarVisibleId}
        />
      )}

      {!!slotOnModal && (
        <ConfirmReserveModal
          onBackdropPress={handleCloseConfirmationModal}
          onBackButtonPress={handleCloseConfirmationModal}
          onSwipeStart={handleCloseConfirmationModal}
          slot={slotOnModal}
          pickerDisabled={timePickerVisible}
          recurrenceType={recurrenceType}
          setRecurrenceType={setRecurrenceType}
        />
      )}
    </>
  );
}
